/**
 * `beforeinstallprompt` capture hook (Phase 3, §5.6).
 *
 * Chromium fires `beforeinstallprompt` when the PWA is installable; we
 * `preventDefault` it (so the browser's mini-infobar is suppressed) and stash
 * the event so a later user gesture can trigger the native prompt. iOS Safari
 * never fires the event, so there install is a static "Teilen → Zum
 * Home-Bildschirm" hint. `appinstalled` clears the affordance; standalone
 * detection hides it once the app already runs installed.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface InstallPromptState {
  /** True once a `beforeinstallprompt` event has been captured (non-iOS). */
  canInstall: boolean;
  /** True on iOS Safari, where install is a manual "Teilen → Home" hint. */
  isIOS: boolean;
  /** True when already running as an installed standalone app. */
  isStandalone: boolean;
  /** Show the native install prompt (no-op until an event is captured). */
  promptInstall: () => Promise<void>;
}

function detectIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/iphone|ipad|ipod/i.test(ua)) return true;
  // iPadOS 13+ masquerades as desktop Safari but is touch-capable.
  return ua.includes('Macintosh') && typeof document !== 'undefined' && 'ontouchend' in document;
}

function detectStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const displayMode = window.matchMedia?.('(display-mode: standalone)').matches ?? false;
  const iosStandalone =
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return displayMode || iosStandalone;
}

export function useInstallPrompt(): InstallPromptState {
  const deferredRef = useRef<BeforeInstallPromptEvent | null>(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isStandalone, setIsStandalone] = useState(detectStandalone);
  const isIOS = useMemo(() => detectIOS(), []);

  useEffect(() => {
    function onBeforeInstallPrompt(event: BeforeInstallPromptEvent) {
      // Suppress the default mini-infobar; we own the affordance.
      event.preventDefault();
      deferredRef.current = event;
      setCanInstall(true);
    }
    function onInstalled() {
      deferredRef.current = null;
      setCanInstall(false);
      setIsStandalone(true);
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    const event = deferredRef.current;
    if (!event) return;
    await event.prompt();
    try {
      await event.userChoice;
    } catch {
      // The choice promise can reject if the prompt is dismissed abruptly; the
      // event is single-use regardless, so we just drop it below.
    }
    deferredRef.current = null;
    setCanInstall(false);
  }, []);

  return useMemo(
    () => ({ canInstall, isIOS, isStandalone, promptInstall }),
    [canInstall, isIOS, isStandalone, promptInstall],
  );
}
